import { Flex, Loader, Pagination } from "@mantine/core";
import {
  type Column,
  flexRender,
  type Row,
  type Table,
} from "@tanstack/react-table";
import Image from "next/image";
import {
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
  useMemo,
} from "react";
import { twMerge } from "tailwind-merge";

import { columnSizingHandler } from "@/components/table/utils";
import { useUserContext } from "@/context/user.context";
import {
  type FilterParamsHandle,
  type ParamsConstraint,
} from "@/hooks/use-filter-params";
import { useImpersonationBannerHeight } from "@/hooks/use-impersonation-banner-height";
import { SortOrder } from "@/types/api";
import type { ScreenerLocation } from "@/types/screener-location";
import type { Screener, Screening } from "@/types/screeners";
import { getTableViewContentHeight, LAYOUT_DIMENSIONS } from "@/utils/ui";

type TableDataType = Screener | ScreenerLocation | Screening;

interface TableSharedWrapperProps<
  T extends TableDataType,
  F extends ParamsConstraint,
> {
  emptyStateUI: ReactNode;
  filterParams: FilterParamsHandle<F>;
  filters: ReactNode;
  // The loading indicator appears on the first header of the table. For the job
  // table this would be the row selection checkbox. Allow passing an override
  // to place the loader in a cell with text
  defaultDescendingSortColumns?: readonly string[];
  firstTrueHeaderIndex?: number;
  getPinningStyles: (column: Column<T>, row?: Row<T>) => CSSProperties;
  // We use isFetched to prevent UI flashes on initial load
  isFetched: boolean;
  isLoading: boolean;
  page: number;
  setPage: (page: number) => void;
  sortableColumns: readonly string[];
  table: Table<T>;
  totalPages: number;
}

const PAGINATION_HEIGHT_PX = 64;

export const TableSharedWrapper = <
  T extends TableDataType,
  F extends ParamsConstraint,
>({
  children,
  emptyStateUI,
  filterParams,
  filters,
  defaultDescendingSortColumns = [],
  firstTrueHeaderIndex = 0,
  getPinningStyles,
  isFetched,
  isLoading,
  page,
  setPage,
  sortableColumns,
  table,
  totalPages,
}: PropsWithChildren<TableSharedWrapperProps<T, F>>) => {
  const { user } = useUserContext();
  const rows = table.getFilteredRowModel().rows;
  const showPagination = totalPages > 1;
  const impersonationBannerHeight = useImpersonationBannerHeight();
  const tableContainerMaxHeight = useMemo(
    () =>
      LAYOUT_DIMENSIONS.combinedHeaderHeight +
      impersonationBannerHeight +
      (showPagination ? PAGINATION_HEIGHT_PX : 0),
    [impersonationBannerHeight, showPagination],
  );

  return (
    <div className="flex w-full">
      {filters}
      <div
        style={{
          width: `calc(100dvw - ${LAYOUT_DIMENSIONS.FILTER_MENU_WIDTH_PX}px)`,
        }}
      >
        <div
          className="overflow-scroll w-full"
          style={{
            maxHeight: `calc(100dvh - ${tableContainerMaxHeight}px)`,
          }}
        >
          {rows.length < 1 ? (
            <Flex
              align="center"
              className={twMerge(["w-full"])}
              direction="column"
              justify="center"
              style={{ height: getTableViewContentHeight(user?.isInternal) }}
            >
              {!isFetched || isLoading ? <Loader /> : emptyStateUI}
            </Flex>
          ) : (
            <>
              <div className="h-1" />
              <table className="w-full min-w-max border-l border-r border-gray-200 text-sm">
                <thead className="align-start">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      className="shadow-[0_1px_0_0_rgba(217,217,217,1)] sticky top-0 z-50"
                    >
                      {headerGroup.headers.map((header, index) => {
                        const isSortableHeader = sortableColumns.includes(
                          header.id,
                        );
                        return (
                          <th
                            colSpan={header.colSpan}
                            className={twMerge([
                              "p-3",
                              "text-start",
                              "uppercase",
                              "font-semibold",
                              "text-xs",
                              "truncate",
                            ])}
                            key={header.id}
                            ref={(th) =>
                              columnSizingHandler(th, table, header.column)
                            }
                            style={getPinningStyles(header.column)}
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                className={twMerge(
                                  isSortableHeader && [
                                    "cursor-pointer",
                                    "select-none",
                                    "flex",
                                    "items-center",
                                    "gap-2",
                                  ],
                                )}
                                onClick={() => {
                                  if (!isSortableHeader) {
                                    return;
                                  }

                                  if (
                                    filterParams.get("sortBy" as keyof F) ===
                                    header.id
                                  ) {
                                    const order = filterParams.get(
                                      "sortOrder" as keyof F,
                                    );

                                    filterParams.set(
                                      "sortOrder" as keyof F,
                                      (order === SortOrder.Asc
                                        ? SortOrder.Desc
                                        : SortOrder.Asc) as F[keyof F],
                                    );
                                  } else {
                                    filterParams.set(
                                      "sortBy" as keyof F,
                                      header.id as F[keyof F],
                                    );
                                    filterParams.set(
                                      "sortOrder" as keyof F,
                                      (defaultDescendingSortColumns.includes(
                                        header.id,
                                      )
                                        ? SortOrder.Desc
                                        : SortOrder.Asc) as F[keyof F],
                                    );
                                  }
                                }}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                                {filterParams.get("sortBy" as keyof F) ===
                                header.id
                                  ? {
                                      [SortOrder.Asc]: (
                                        <div className="w-full flex items-center">
                                          <Image
                                            src="/assets/SortAscending.svg"
                                            width={12}
                                            height={12}
                                            alt="sort"
                                          />
                                        </div>
                                      ),
                                      [SortOrder.Desc]: (
                                        <div className="w-full flex items-center">
                                          <Image
                                            src="/assets/SortDescending.svg"
                                            width={12}
                                            height={12}
                                            alt="sort"
                                          />
                                        </div>
                                      ),
                                    }[
                                      filterParams.getWithDefault(
                                        "sortOrder" as keyof F,
                                        SortOrder.Desc as F[keyof F],
                                      ) as SortOrder
                                    ]
                                  : isSortableHeader && (
                                      <div className="w-full flex items-center">
                                        <Image
                                          src="/assets/sort.svg"
                                          width={12}
                                          height={12}
                                          alt="sort"
                                        />
                                      </div>
                                    )}
                                {isLoading &&
                                  index === firstTrueHeaderIndex && (
                                    <Loader size="xs" />
                                  )}
                              </div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody>{children}</tbody>
              </table>
            </>
          )}
        </div>
        {showPagination && (
          <div
            className="w-full flex justify-center items-center"
            style={{ height: PAGINATION_HEIGHT_PX }}
          >
            <Pagination
              onChange={(value) => setPage(value)}
              total={totalPages}
              value={page}
            />
          </div>
        )}
      </div>
    </div>
  );
};
